import { getApiBaseUrl } from '../config/api';
import { getFirestore, collection, query, where, orderBy, onSnapshot, Unsubscribe } from 'firebase/firestore';

export interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  role?: string;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  recipientId: string;
  content: string;
  messageType: 'TEXT' | 'IMAGE' | 'FILE' | 'SYSTEM';
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  sender: User;
}

export interface Conversation {
  id: string;
  otherUser: User;
  lastMessage: {
    id: string;
    content: string;
    createdAt: string;
    isRead: boolean;
    senderId: string;
  } | null;
  unreadCount: number;
  lastMessageAt: string;
}

export interface SendMessageData {
  recipientId: string;
  content: string;
  messageType?: 'TEXT' | 'IMAGE' | 'FILE';
}

// FEAT-058 (MVP-18.55) — the send response carries a `meta` block telling the
// client whether contact info (email/phone/URL) was redacted from the body so
// the thread can show a non-alarming inline notice. The flag itself is
// admin-controlled (FEAT-059); clients only react to this meta.
export interface RedactionCounts {
  email: number;
  phone: number;
  url: number;
}

export interface SendMessageMeta {
  contactInfoRedacted: boolean;
  redactionCounts: RedactionCounts;
}

export interface SendMessageResult {
  message: Message;
  meta: SendMessageMeta;
}

// FEAT-068 (MVP-18.9)
export interface IceBreakerTemplate {
  id: string;
  label: string;
  body: string;
  mvpRef?: string;
}

const EMPTY_REDACTION_META: SendMessageMeta = {
  contactInfoRedacted: false,
  redactionCounts: { email: 0, phone: 0, url: 0 },
};

class MessageService {
  private normalizeDateValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number') {
      return new Date(value).toISOString();
    }

    if (value && typeof value === 'object') {
      const valueWithToDate = value as { toDate?: () => Date; seconds?: number };

      if (typeof valueWithToDate.toDate === 'function') {
        return valueWithToDate.toDate().toISOString();
      }

      if (typeof valueWithToDate.seconds === 'number') {
        return new Date(valueWithToDate.seconds * 1000).toISOString();
      }
    }

    return new Date().toISOString();
  }

  private createIdempotencyKey(prefix: string): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}-${crypto.randomUUID()}`;
    }

    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Create or resume a conversation from profile-entry flow
   */
  async createConversation(participantId: string): Promise<{ id: string; created: boolean }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/messages/conversations`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ participantId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create conversation');
    }

    const result = await response.json();
    return result.data;
  }

  /** FEAT-068 (MVP-18.9) — curated ice-breaker templates for new conversations. */
  async getIceBreakers(): Promise<IceBreakerTemplate[]> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/messages/ice-breakers`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to load ice-breaker templates');
    }

    const result = await response.json();
    return result.data as IceBreakerTemplate[];
  }

  /**
   * Send a message to another user
   */
  async sendMessage(data: SendMessageData): Promise<SendMessageResult> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/messages/send`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Idempotency-Key': this.createIdempotencyKey('web-message-send'),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send message');
    }

    const result = await response.json();
    return {
      message: result.data,
      meta: this.normalizeSendMeta(result.meta),
    };
  }

  private normalizeSendMeta(meta: unknown): SendMessageMeta {
    if (!meta || typeof meta !== 'object') {
      return EMPTY_REDACTION_META;
    }

    const candidate = meta as Partial<SendMessageMeta>;
    const counts = candidate.redactionCounts ?? EMPTY_REDACTION_META.redactionCounts;

    return {
      contactInfoRedacted: candidate.contactInfoRedacted === true,
      redactionCounts: {
        email: Number(counts.email) || 0,
        phone: Number(counts.phone) || 0,
        url: Number(counts.url) || 0,
      },
    };
  }

  /**
   * Get all conversations for the current user
   */
  async getConversations(page: number = 1, limit: number = 20): Promise<{
    conversations: Conversation[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/messages/conversations?page=${page}&limit=${limit}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch conversations');
    }

    const result = await response.json();
    return {
      conversations: result.data,
      pagination: result.pagination,
    };
  }

  /**
   * Get messages in a conversation
   */
  async getMessages(conversationId: string, page: number = 1, limit: number = 50): Promise<{
    messages: Message[];
    pagination: any;
  }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/messages/conversation/${conversationId}?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch messages');
    }

    const result = await response.json();
    return {
      messages: result.data,
      pagination: result.pagination,
    };
  }

  /**
   * Get messages with a specific user
   */
  async getMessagesWithUser(userId: string, page: number = 1): Promise<{
    conversationId: string;
    messages: Message[];
    pagination: any;
  }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/messages/with/${userId}?page=${page}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch messages');
    }

    const result = await response.json();
    return {
      conversationId: result.data.conversationId,
      messages: result.data.messages,
      pagination: result.pagination,
    };
  }

  /**
   * Mark messages in a conversation as read
   */
  async markAsRead(conversationId: string): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/messages/mark-read/${conversationId}`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Idempotency-Key': this.createIdempotencyKey('web-message-read'),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to mark messages as read');
    }
  }

  /**
   * Get unread message count
   */
  async getUnreadCount(): Promise<number> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/messages/unread-count`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch unread count');
    }

    const result = await response.json();
    return result.data.unreadCount;
  }

  /**
   * Delete a message
   */
  async deleteMessage(messageId: string): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/messages/${messageId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete message');
    }
  }

  /**
   * Subscribe to real-time messages for a conversation using Firestore
   */
  subscribeToConversationMessages(
    conversationId: string,
    callback: (messages: Message[]) => void
  ): Unsubscribe {
    try {
      const db = getFirestore();
      const messagesRef = collection(db, 'messages');
      const q = query(
        messagesRef,
        where('conversationId', '==', conversationId),
        orderBy('createdAt', 'asc')
      );

      return onSnapshot(q, (snapshot) => {
        const messages: Message[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.isDeleted === true) {
            return;
          }

          messages.push({
            id: doc.id,
            conversationId: data.conversationId,
            senderId: data.senderId,
            recipientId: data.recipientId,
            content: data.content,
            messageType: data.messageType,
            isRead: data.isRead,
            readAt: data.readAt ? this.normalizeDateValue(data.readAt) : null,
            createdAt: this.normalizeDateValue(data.createdAt),
            sender: data.sender,
          });
        });
        callback(messages);
      });
    } catch (error) {
      console.error('Error subscribing to messages:', error);
      // Return a no-op unsubscribe function if Firestore fails
      return () => {};
    }
  }

  /**
   * Subscribe to unread count updates using Firestore
   */
  subscribeToUnreadCount(userId: string, callback: (count: number) => void): Unsubscribe {
    try {
      const db = getFirestore();
      const messagesRef = collection(db, 'messages');
      const q = query(
        messagesRef,
        where('recipientId', '==', userId),
        where('isRead', '==', false)
      );

      return onSnapshot(q, (snapshot) => {
        const unreadCount = snapshot.docs.reduce((count, doc) => {
          const data = doc.data();
          return data.isDeleted === true ? count : count + 1;
        }, 0);
        callback(unreadCount);
      });
    } catch (error) {
      console.error('Error subscribing to unread count:', error);
      // Return a no-op unsubscribe function if Firestore fails
      return () => {};
    }
  }
}

export const messageService = new MessageService();

